import { z } from "zod";
import { STTDomain } from "~/utils/signalr-types";
import { DEFAULT_MODEL_VALUE } from "~/utils/stt-models";
import { supportedLanguages } from "~/utils/supported-languages";

const ROOM_NAME_MIN_LENGTH = 5;
const ROOM_NAME_MAX_LENGTH = 50;

/**
 * One schema for the whole wizard: react-hook-form keeps a single form state
 * across steps, and each step validates its own slice via `form.trigger`.
 */
export function getInitialStartAndEndTimes() {
	const now = new Date();
	const minutes = now.getMinutes();
	// Round to the next 15-minute block (e.g. 14:07 -> 14:15)
	const roundedMinutes = Math.ceil(minutes / 15) * 15;
	const startTime = new Date(now);
	startTime.setMinutes(roundedMinutes, 0, 0);

	const endTime = new Date(startTime.getTime() + 30 * 60 * 1000);
	return { startTime, endTime };
}

export const createRoomSchema = z
	.object({
		name: z
			.string({ required_error: "Room name is required." })
			.min(ROOM_NAME_MIN_LENGTH, {
				message: `Room name should be at least ${ROOM_NAME_MIN_LENGTH} characters.`,
			})
			.max(ROOM_NAME_MAX_LENGTH, {
				message: `Room name cannot exceed ${ROOM_NAME_MAX_LENGTH} characters.`,
			}),
		language: z.enum([...supportedLanguages, "auto"]),
		startTime: z.date({ required_error: "Start time is required." }),
		endTime: z.date({ required_error: "End time is required." }),
		/** `<Select>` value from the model catalogue, not the raw provider. */
		model: z.string(),
		sttDomain: z.nativeEnum(STTDomain),
		modelLocked: z.boolean(),
	})
	.refine(
		(data) => data.endTime.getTime() >= data.startTime.getTime() + 15 * 60_000,
		{
			message: "End time must be at least 15 minutes after start time.",
			path: ["endTime"],
		}
	);

export type CreateRoomFormValues = z.infer<typeof createRoomSchema>;

/** Fields owned by step one, validated before the wizard advances. */
export const ROOM_DETAILS_FIELDS = [
	"name",
	"language",
	"startTime",
	"endTime",
] as const satisfies readonly (keyof CreateRoomFormValues)[];

export const getCreateRoomDefaultValues = () => {
	const { startTime, endTime } = getInitialStartAndEndTimes();
	return {
		name: "",
		startTime,
		endTime,
		model: DEFAULT_MODEL_VALUE,
		// Math Optimized is opt-out: on by default, but only reaches the room when
		// the chosen model actually supports a domain.
		sttDomain: STTDomain.Math,
		modelLocked: true,
	} satisfies Partial<CreateRoomFormValues>;
};
